// pages/MyShifts.jsx
import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../contexts/auth";
import ShiftTableDailyShow from "../components/shift_table_daily_show";
import ShiftTableWeeklyShow from "../components/shift_table_weekly_show";
import Footer from "../components/footer";
import PopoverComponentCustom from "../components/popover_component";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { getCompanySettings } from "../services/api_settings";
import { getWorkingHoursCompound, getWeeklyWorkingHourCompound } from "../services/api_working_hours";
import { listOrganizationalUnits } from "../services/api_org_unit";
import PrettyPagination from "../components/pretty_pagination";
import CompanyContextBar from '../components/shift_company_context_bar';

export default function MyShiftsCompany() {
  const { token } = useContext(AuthContext);

  const [shiftType, setShiftType] = useState("GIORNALIERO"); // "GIORNALIERO" | "SETTIMANALE"
  const [workingHours, setWorkingHours] = useState([]);
  const [weeklyWorkingHours, setWeeklyWorkingHours] = useState([]);
  const [loadingDaily, setLoadingDaily] = useState(false);
  const [loadingWeekly, setLoadingWeekly] = useState(false);
  const [allowHiddenBreaks, setAllowHiddenBreaks] = useState(false);
  const [units, setUnits] = useState([]);
  const [isUnitsLoading, setIsUnitsLoading] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [search, setSearch] = useState("");
  const PAGE_SIZE = 25;

  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const handlePickType = (t) => setShiftType(t === "daily" ? "GIORNALIERO" : "SETTIMANALE");

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil((totalCount || 0) / PAGE_SIZE));
  }, [totalCount]);

  useEffect(() => {
    setPage(1);
  }, [shiftType, selectedUnit, search]);

  // settings
  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    (async () => {
      try {
        const settings = await getCompanySettings(token, "payrolls");
        const allowHidden = Boolean(settings?.allow_hidden_breaks ?? false);
        if (!cancelled) setAllowHiddenBreaks(allowHidden);
      } catch {
        if (!cancelled) setAllowHiddenBreaks(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const ouParam = useMemo(() => {
    if (!selectedUnit) return undefined;            // Generici => niente ou
    if (selectedUnit.id === "__ALL__") return "all"; // Tutti
    return selectedUnit.id;                         // OU specifica
  }, [selectedUnit]);

  useEffect(() => {
    if (!token) return;

    const controller = new AbortController();
    const { signal } = controller;

    const fetchData = async () => {
      try {
        if (shiftType === "GIORNALIERO") {
          setLoadingDaily(true);
          setLoadingWeekly(false);

          const res = await getWorkingHoursCompound(
            token,
            ouParam,
            "shifts",
            page,
            PAGE_SIZE,
            search,
            true,
            signal
          );

          if (signal.aborted) return;

          setWorkingHours(Array.isArray(res?.results) ? res.results : []);
          setTotalCount(typeof res?.count === "number" ? res.count : 0);
        } else {
          setLoadingWeekly(true);
          setLoadingDaily(false);

          const res = await getWeeklyWorkingHourCompound(
            token,
            ouParam,
            null,
            "shifts",
            page,
            PAGE_SIZE,
            search,
            true,
            signal 
          );

          if (signal.aborted) return;

          setWeeklyWorkingHours(Array.isArray(res?.results) ? res.results : []);
          setTotalCount(typeof res?.count === "number" ? res.count : 0);
        }
      } catch (e) {
        // axios abort -> CanceledError
        if (signal.aborted || e?.name === "CanceledError" || e?.name === "AbortError") return;
        console.error(e);
      } finally {
        if (!signal.aborted) {
          setLoadingDaily(false);
          setLoadingWeekly(false);
        }
      }
    };

    fetchData();

    return () => controller.abort(); // aborta al cambio di tab/filtri/pagina
  }, [token, shiftType, ouParam, page, PAGE_SIZE, search]);


  useEffect(() => {
    if (!token) return;
    setIsUnitsLoading(true);
    listOrganizationalUnits(token, true)
      .then((res) => setUnits(res?.data || []))
      .catch((e) => console.error(e))
      .finally(() => setIsUnitsLoading(false));
  }, [token]);

  const isEmpty = useMemo(() => {
    if (shiftType === "GIORNALIERO") return !loadingDaily && workingHours.length === 0;
    return !loadingWeekly && weeklyWorkingHours.length === 0;
  }, [shiftType, loadingDaily, loadingWeekly, workingHours, weeklyWorkingHours]);

  const type = shiftType === "GIORNALIERO" ? "daily" : "weekly";

  return (
    <div className="flex flex-col min-h-screen bg-gray-50/40">
      <PopoverComponentCustom title="I miei turni" />

      <main className="mx-auto w-full max-w-[1600px] px-8 flex-grow mt-10 pb-8">
        {/* Header pagina */}
        <div className="mb-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div >
              <h1 className="text-md font-semibold uppercase text-indigo-800">I miei turni</h1>
              <p className="text-sm text-gray-500">Visualizza e gestisci i turni giornalieri e settimanali creati</p>
            </div>

            {/* Switch bottoni pill */}
            <div className="self-start sm:self-auto">
              <div className="inline-flex rounded-xl shadow-sm overflow-hidden border border-indigo-100">
                <button
                  type="button"
                  onClick={() => handlePickType("daily")}
                  className={`px-4 py-2 text-sm font-semibold transition flex items-center gap-2 focus:outline-none
                    ${type === "daily"
                      ? "bg-indigo-600 text-white"
                      : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                    }`}
                >
                  <AccessTimeOutlinedIcon sx={{ fontSize: 18 }} />
                  GIORNALIERO
                </button>

                <button
                  type="button"
                  onClick={() => handlePickType("weekly")}
                  className={`px-4 py-2 text-sm font-semibold transition border-l flex items-center gap-2 focus:outline-none
                    ${type === "weekly"
                      ? "bg-indigo-600 text-white border-indigo-600"
                      : "bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border-indigo-200"
                    }`}
                >
                  <CalendarTodayOutlinedIcon sx={{ fontSize: 18 }} />
                  SETTIMANALE
                </button>
              </div>

              <div className="text-xs text-gray-500 leading-snug mt-2">
                Seleziona la tipologia di turno che vuoi visualizzare
              </div>
            </div>
          </div>
        </div>
        <div className="sticky top-0 z-40 bg-white pt-2 pb-6">
          <CompanyContextBar
            units={units}
            selectedUnit={selectedUnit}
            onSelectUnit={setSelectedUnit}
            onResetUnit={() => setSelectedUnit(null)}
            isUnitsLoading={isUnitsLoading}
            search={search}
            onSearchChange={setSearch}
            shiftTypeLabel={shiftType}
          />
        </div>

        {/* Card contenuto */}
        <div className="mt-4 ">
          <div className="">
            {isEmpty ? (
              <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 p-6 text-center">
                <div className="text-sm font-semibold text-gray-800">
                  Nessun turno trovato
                </div>
                <div className="mt-1 text-xs text-gray-600">
                  Prova a cambiare unità oppure crea un nuovo turno
                </div>
              </div>
            ) : (
              <>
                {shiftType === "GIORNALIERO" && (
                  <>
                    <ShiftTableDailyShow
                      workingHours={workingHours}
                      loading={loadingDaily}
                      selectedUnit={selectedUnit}
                      allowHiddenBreaks={allowHiddenBreaks}
                      onUpdateWorkingHours={setWorkingHours}
                      onDeleteShift={(id) => setWorkingHours((prev) => prev.filter((x) => x.id !== id))}
                      whCount={totalCount}
                    />
                    {!loadingDaily && (
                      <div className="py-4">
                        <PrettyPagination
                          page={page}
                          totalPages={totalPages}
                          totalCount={totalCount}
                          onChange={setPage}
                          labelSingolare="turno"
                          labelPlurale="turni"
                        />
                      </div>
                    )}
                  </>
                )}

                {shiftType === "SETTIMANALE" && (
                  <>
                    <ShiftTableWeeklyShow
                      weeklyWorkingHours={weeklyWorkingHours}
                      loading={loadingWeekly}
                      selectedUnit={selectedUnit}
                      whCount={totalCount}
                      allowHiddenBreaks={allowHiddenBreaks}
                      onDeleteWeekShift={(id) => setWeeklyWorkingHours((prev) => prev.filter((x) => x.id !== id))}
                    />
                    {!loadingWeekly && (
                      <div className="mt-4">
                        <PrettyPagination
                          page={page}
                          totalPages={totalPages}
                          totalCount={totalCount}
                          onChange={setPage}
                          labelSingolare="turno"
                          labelPlurale="turni"
                        />
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}